import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Checkbox, Separator } from '@roger/ui-components';
import { Stethoscope } from 'lucide-react';
import type { UserType } from '../../services/api/models/UserType';
import { USER_TYPE_OPTIONS } from '../../constants/userTypes';
import { updateUserType } from '../../services/api';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { RoleAssignmentSaveError } from '../rbac/role-assignment-dialog/useRoleAssignmentDialog';

export type UserTypeSectionHandle = {
  saveIfDirty: () => Promise<void>;
};

type UserTypeSectionProps = {
  userId: string;
  /** Every title held, primary first. */
  userTypes: UserType[] | null | undefined;
  /** When false, omit the leading separator (e.g. first body section). */
  showTopSeparator?: boolean;
};

const sameTitles = (a: UserType[], b: UserType[]): boolean =>
  a.length === b.length && a.every((title, index) => title === b[index]);

export const UserTypeSection = React.memo(
  React.forwardRef<UserTypeSectionHandle, UserTypeSectionProps>(
    function UserTypeSection(
      { userId, userTypes, showTopSeparator = true },
      ref
    ) {
      const { showError } = useStore(
        useShallow(state => ({ showError: state.showError }))
      );
      const saved = userTypes ?? [];
      const [values, setValues] = useState<UserType[]>(saved);

      useEffect(() => {
        setValues(userTypes ?? []);
      }, [userTypes]);

      // Order matters, not just membership: the first title is the primary one.
      const isDirty = !sameTitles(values, saved);

      useImperativeHandle(
        ref,
        () => ({
          saveIfDirty: async () => {
            if (!isDirty) return;
            try {
              await updateUserType({
                path: { user_id: userId },
                body: { user_types: values },
                throwOnError: true,
              });
            } catch (error) {
              console.error('Failed to update clinical titles:', error);
              showError(
                'Save Failed',
                'Unable to update clinical titles. Please try again.'
              );
              throw new RoleAssignmentSaveError('user type save failed');
            }
          },
        }),
        [isDirty, showError, userId, values]
      );

      const toggle = (title: UserType) => {
        setValues(previous =>
          previous.includes(title)
            ? previous.filter(held => held !== title)
            : [...previous, title]
        );
      };

      return (
        <div className='shrink-0'>
          {showTopSeparator && <Separator className='my-4' />}
          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <Stethoscope className='h-4 w-4 text-muted-foreground' />
              <h3 className='text-sm font-semibold'>
                Clinical Title
                <span className='ml-2 font-normal text-muted-foreground'>
                  — check every title they hold
                </span>
              </h3>
            </div>
            <div
              className='flex flex-wrap gap-3'
              role='group'
              aria-label='Clinical titles'
            >
              {USER_TYPE_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className='flex items-center gap-2 text-sm'
                >
                  <Checkbox
                    checkboxSize='sm'
                    checked={values.includes(option.value)}
                    onCheckedChange={() => toggle(option.value)}
                  />
                  <span className='whitespace-nowrap'>{option.label}</span>
                </label>
              ))}
            </div>
            {values.length > 1 && (
              // Which one is primary is not cosmetic: it renders in
              // "Name, RN" and orders the schedule report.
              <p className='text-xs text-muted-foreground'>
                {USER_TYPE_OPTIONS.find(o => o.value === values[0])?.label ??
                  values[0]}{' '}
                is the primary title. Uncheck and re-check to change it.
              </p>
            )}
          </div>
          <Separator className='my-4' />
        </div>
      );
    }
  )
);
